import {
  QuestionType,
  StudentSource,
  type ConversationOptionsResource,
  type ConversationOptionsResourceQuestionTypesItem,
  type ConversationOptionsResourceStudentSourcesItem,
} from "api/generated";
import type {
  ConversationOption,
  ConversationOptions,
} from "conversations/types";

/**
 * The API types an option's `value` as a plain string, because it is built from the
 * enum's value rather than declared as the enum. Narrowing here — instead of casting —
 * means an option the frontend does not know is dropped rather than smuggled into a
 * typed field, which is what would happen if the backend gained a case first.
 */
const narrowOptions = <TValue extends string>(
  items: (
    | ConversationOptionsResourceStudentSourcesItem
    | ConversationOptionsResourceQuestionTypesItem
  )[],
  known: Record<string, TValue>,
): ConversationOption<TValue>[] => {
  const values = Object.values<TValue>(known);

  return items
    .filter((item): item is { value: TValue; label: string } =>
      values.includes(item.value as TValue),
    )
    .map((item) => ({ value: item.value, label: item.label }));
};

/** Maps `GET api/conversation-options` (ConversationOptionsResource). */
export const conversationOptionsFromDto = (
  dto: ConversationOptionsResource,
): ConversationOptions => ({
  studentSources: narrowOptions(dto.student_sources, StudentSource),
  questionTypes: narrowOptions(dto.question_types, QuestionType),
});
